/**
 * The MCP server windows (slice 3b spec §6.1, §6.2, §6.4; Paper 42, 42b, 43,
 * 43b, 46, 46b): Add MCP server, empty or filled in from a directory entry; a
 * server's tools, each On or Off; and Reconnect with a new key. A dialog on a
 * desktop, a sheet from the bottom on a phone, as the connect window is.
 *
 * An add that doesn't work shows why at the top of the window, the fields
 * kept (Paper 44, "Add failed"); nothing was added. One that works opens the
 * server's tools, for the owner to review; OAuth goes to sign in first.
 */
import { Button } from "@crew/ui/components/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@crew/ui/components/dialog";
import { Input } from "@crew/ui/components/input";
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from "@crew/ui/components/drawer";
import { Spinner } from "@crew/ui/components/spinner";
import { Switch } from "@crew/ui/components/switch";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { cn } from "@crew/ui/lib/utils";
import { ArrowUpRight, Check, ChevronLeft, Lock } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { ApiError, errorHint } from "../../app/api";
import { DocMarkdown } from "../docs/doc-markdown";
import {
  useAddServer,
  useChangeGuide,
  useGuideText,
  useReconnectServer,
  useRefreshServer,
  useServerTools,
  useSetTool,
  useSetTools,
  type DirectoryEntry,
  type McpAuth,
  type McpGuide,
  type McpGuideText,
  type McpServer,
  type McpTool,
} from "./integrations-api";
import { ToolAskFirst } from "./ask-first-page";
import { AppIcon } from "./integrations-parts";
import {
  AUTH_WORDS,
  EMPTY_SERVER_FORM,
  directoryForm,
  directoryNote,
  guideBody,
  guideTab,
  serverBody,
  serverFormProblems,
  toolCounts,
  toolsNote,
  toolTag,
  type ServerForm,
  type Tone,
} from "./integrations-view";
import { openSignInTab } from "./signin-tab";

// ── The frame ─────────────────────────────────────────────────────────────

/** A dialog on a desktop, a sheet on a phone. While `locked`, nothing closes it. */
function Panel({
  open,
  locked = false,
  onClose,
  width = 580,
  children,
}: {
  open: boolean;
  locked?: boolean;
  onClose: () => void;
  width?: number;
  children: ReactNode;
}) {
  const mobile = useIsMobile();
  const close = () => {
    if (!locked) onClose();
  };
  if (mobile) {
    return (
      <Drawer open={open} onOpenChange={(o) => !o && close()} showSwipeHandle>
        <DrawerContent
          className="data-[swipe-direction=down]:rounded-t-2xl"
          showCloseButton={!locked}
        >
          {children}
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent
        className="max-h-[90svh] gap-0 overflow-y-auto p-0"
        style={{ maxWidth: width }}
        showCloseButton={!locked}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
}

/** The window's head: the server's tile on a desktop, its title and one line. */
function Head({ title, line, icon = true }: { title: string; line: ReactNode; icon?: boolean }) {
  const mobile = useIsMobile();
  const TitleEl = mobile ? DrawerTitle : DialogTitle;
  const LineEl = mobile ? DrawerDescription : DialogDescription;
  return (
    <div className="flex items-start gap-3.5 px-5 pt-5 pb-4.5 md:px-6 md:pt-6">
      {icon ? (
        <span className="hidden md:block">
          <AppIcon app="mcp:" size="lg" />
        </span>
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col gap-1 pr-6">
        <TitleEl className="text-[20px] leading-[26px] font-semibold tracking-tight text-foreground">
          {title}
        </TitleEl>
        <LineEl className="text-sm text-muted-foreground">{line}</LineEl>
      </div>
    </div>
  );
}

/**
 * The action, and beside it Cancel (or `secondary`): side by side with a note
 * on a desktop, stacked full width on a phone.
 */
function Footer({
  note,
  primary,
  busy,
  onClose,
  secondary,
}: {
  note: string;
  primary: ReactNode;
  busy: boolean;
  onClose: () => void;
  /** In Cancel's place; null for none. */
  secondary?: ReactNode;
}) {
  const mobile = useIsMobile();
  const second =
    secondary === undefined ? (
      <Button variant="ghost" size="lg" onClick={onClose} disabled={busy} className="h-11 md:h-9">
        Cancel
      </Button>
    ) : (
      secondary
    );
  if (mobile) {
    return (
      <div className="flex flex-col gap-1 border-t border-border px-4 pt-4 pb-6">
        <div className="flex flex-col [&>button]:h-11 [&>button]:w-full">{primary}</div>
        {second}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 rounded-b-xl border-t border-border bg-foreground/[0.02] px-6 py-3.5">
      <span className="flex-1 text-meta text-muted-foreground">{note}</span>
      {second}
      {primary}
    </div>
  );
}

// ── Add MCP server ────────────────────────────────────────────────────────

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-foreground">{label}</span>
      {children}
      {error ? (
        <span className="text-xs text-destructive">{error}</span>
      ) : hint ? (
        <span className="text-xs text-muted-foreground">{hint}</span>
      ) : null}
    </label>
  );
}

const AUTHS: McpAuth[] = ["none", "api_key", "oauth"];

/** None, API key, OAuth (Paper 42): one chosen. */
function AuthPicker({ value, onChange }: { value: McpAuth; onChange: (a: McpAuth) => void }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-foreground" id="mcp-auth">
        How it signs in
      </span>
      <div
        role="radiogroup"
        aria-labelledby="mcp-auth"
        className="flex rounded-lg bg-secondary p-0.5"
      >
        {AUTHS.map((a) => (
          <button
            key={a}
            type="button"
            role="radio"
            aria-checked={value === a}
            onClick={() => onChange(a)}
            className={cn(
              "h-8.5 flex-1 rounded-md text-sm",
              value === a
                ? "bg-card font-semibold text-foreground shadow-[0_0_0_1px_rgb(31_42_68/0.08),0_1px_2px_rgb(31_42_68/0.08)]"
                : "text-muted-foreground",
            )}
          >
            {a === "none" ? "None" : AUTH_WORDS[a]}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The directory entry the form came from (Paper 46): Checked with Crew, or why it may not work. */
function DirectoryNote({ entry }: { entry: DirectoryEntry }) {
  const note = directoryNote(entry);
  return (
    <div
      data-directory-note={entry.checked ? "checked" : "not-checked"}
      className="flex items-center gap-3 rounded-lg bg-foreground/[0.03] px-4 py-3 shadow-[0_0_0_1px_rgb(31_42_68/0.08)]"
    >
      <span className="hidden md:block">
        <AppIcon app="directory" logo={entry.logo} mark={entry.name.slice(0, 2)} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm font-semibold text-foreground">{note.title}</span>
        <span
          className={cn(
            "text-meta leading-[18px]",
            note.checked ? "text-muted-foreground md:hidden" : "text-copy",
          )}
        >
          {note.checked ? (
            <span className="flex items-center gap-1 text-moss">
              <Check className="size-3" /> {note.line}
            </span>
          ) : (
            note.line
          )}
        </span>
      </div>
      {note.checked ? (
        <span className="hidden shrink-0 items-center gap-1 text-meta text-moss md:flex">
          <Check className="size-3" /> {note.line}
        </span>
      ) : null}
    </div>
  );
}

/** An add that didn't work, at the top of the window (Paper 44, "Add failed"). */
function AddFailed({ name, error }: { name: string; error: unknown }) {
  const reached = error instanceof ApiError && error.code === "mcp_unreachable";
  return (
    <div
      role="alert"
      className="flex flex-col gap-1 rounded-lg bg-destructive/[0.04] px-4 py-3 shadow-[0_0_0_1px_rgb(162_59_47/0.25)]"
    >
      <span className="text-sm font-semibold text-destructive">
        {reached ? `Couldn't read ${name || "the server"}'s tools` : "That didn't work"}
      </span>
      <span className="text-meta leading-[18px] text-copy">{errorHint(error)}</span>
    </div>
  );
}

/** The fields of Add MCP server, apart from its frame so a test can render it. */
export function ServerFields({
  form,
  onChange,
  entry,
  error,
  showProblems,
}: {
  form: ServerForm;
  onChange: (f: ServerForm) => void;
  entry: DirectoryEntry | null;
  error: unknown;
  showProblems: boolean;
}) {
  const problems = showProblems ? serverFormProblems(form) : {};
  const set = (patch: Partial<ServerForm>) => onChange({ ...form, ...patch });
  const site = entry?.name ?? "the server";
  return (
    <div className="flex flex-col gap-4 px-5 pb-5 md:px-6">
      {error ? <AddFailed name={form.name} error={error} /> : null}
      {entry ? <DirectoryNote entry={entry} /> : null}
      <Field
        label="Name"
        error={problems.name}
        hint={
          entry
            ? "Your crew knows it by this name. You can change it."
            : "Lowercase letters, numbers and dashes. Your crew knows it by this name."
        }
      >
        <Input
          aria-label="Name"
          value={form.name}
          maxLength={30}
          placeholder="acme-crm"
          onChange={(e) => set({ name: e.target.value.toLowerCase() })}
          className="h-10"
        />
      </Field>
      <Field
        label="Server URL"
        error={problems.url}
        hint={
          entry
            ? `The server ${site} runs. Change it only if ${site} gave you another.`
            : "A public https address, with no password in it."
        }
      >
        <Input
          aria-label="Server URL"
          value={form.url}
          inputMode="url"
          placeholder="https://mcp.example.com/mcp"
          onChange={(e) => set({ url: e.target.value })}
          className="h-10 font-mono text-base md:text-[13px]"
        />
      </Field>
      <AuthPicker value={form.auth} onChange={(auth) => set({ auth })} />
      {form.auth === "api_key" ? (
        <div className="flex flex-col gap-3.5 rounded-lg bg-foreground/[0.02] p-4 shadow-[0_0_0_1px_rgb(31_42_68/0.08)]">
          <Field label="API key" error={problems.apiKey}>
            <Input
              aria-label="API key"
              type="password"
              autoComplete="off"
              value={form.apiKey}
              onChange={(e) => set({ apiKey: e.target.value })}
              className="h-10 bg-card"
            />
          </Field>
          <div className="grid grid-cols-2 gap-2.5">
            <Field label="Header" error={problems.header}>
              <Input
                aria-label="Header"
                value={form.header}
                onChange={(e) => set({ header: e.target.value })}
                className="h-10 bg-card font-mono text-base md:text-[13px]"
              />
            </Field>
            <Field label="Format" error={problems.format}>
              <Input
                aria-label="Format"
                value={form.format}
                onChange={(e) => set({ format: e.target.value })}
                className="h-10 bg-card font-mono text-base md:text-[13px]"
              />
            </Field>
          </div>
          <p className="text-xs leading-[18px] text-muted-foreground">
            Crew keeps the key safe and uses it only to call this server. It never goes on your
            computer.
          </p>
        </div>
      ) : null}
      {form.auth === "oauth" ? (
        <p className="flex items-start gap-2.5 rounded-lg bg-foreground/[0.02] px-4 py-3 text-meta leading-[19px] text-copy shadow-[0_0_0_1px_rgb(31_42_68/0.08)]">
          <Lock className="mt-0.5 size-3.5 shrink-0" />
          You'll sign in to {site} on its own page. There's no key to paste: Crew registers itself
          with the server, and keeps the sign-in safe.
        </p>
      ) : null}
    </div>
  );
}

type AddProps = {
  open: boolean;
  entry: DirectoryEntry | null;
  onClose: () => void;
  onAdded: (server: McpServer) => void;
  /** An OAuth server's sign-in went to its own tab (the window has closed). */
  onSignInOpened?: () => void;
};

/**
 * Add MCP server's form and its submit. Added, `onAdded`; an OAuth sign-in
 * sent to its own tab closes the window.
 */
function useAddForm({ open, entry, onClose, onAdded, onSignInOpened }: AddProps) {
  const add = useAddServer();
  const [form, setForm] = useState<ServerForm>(EMPTY_SERVER_FORM);
  const [tried, setTried] = useState(false);
  // A new open starts from the entry (or empty), and forgets the last error.
  useEffect(() => {
    if (!open) return;
    setForm(entry ? directoryForm(entry) : EMPTY_SERVER_FORM);
    setTried(false);
    add.reset();
    // `add` is stable enough: only a new open resets it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entry]);
  const submit = () => {
    setTried(true);
    if (Object.keys(serverFormProblems(form)).length > 0) return;
    // An OAuth sign-in's tab opens now, in the submit, so no popup blocker stops it.
    const tab = form.auth === "oauth" ? openSignInTab() : null;
    add.mutate(
      { body: serverBody(form), tab },
      {
        onSuccess: (out) => {
          // OAuth has gone to sign in; the tools come when it returns. In its
          // own tab, this window closes and the card waits for it.
          if (!out.url) onAdded(out.server);
          else if (out.inTab) {
            onClose();
            onSignInOpened?.();
          }
        },
      },
    );
  };
  return { add, form, setForm, tried, submit };
}

/**
 * Add MCP server: `entry` fills it in from the directory (spec §6.4); none is
 * Add by URL. Added, it calls `onAdded` with the server, whose tools come next.
 */
export function AddServerWindow(props: AddProps) {
  const { open, entry, onClose } = props;
  const { add, form, setForm, tried, submit } = useAddForm(props);
  const busy = add.isPending;
  const oauth = form.auth === "oauth";
  const site = entry?.name ?? "the server";
  const title = entry ? `Add ${entry.name}` : "Add an MCP server";
  const line = entry
    ? `Filled in from the directory. Check it${oauth ? `, then sign in to ${entry.name}` : ""}.`
    : "A server on the web with tools for your crew. Crew calls it for them, so its key never reaches their computer.";
  return (
    <Panel open={open} locked={busy} onClose={onClose}>
      <form
        data-add-server
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Head title={title} line={line} />
        <ServerFields
          form={form}
          onChange={setForm}
          entry={entry}
          error={add.error}
          showProblems={tried}
        />
        <Footer
          note={
            oauth
              ? "Next, sign in, then choose which of its tools are on."
              : "Next, you'll choose which of its tools are on."
          }
          busy={busy}
          onClose={onClose}
          primary={
            <Button type="submit" size="lg" disabled={busy} className="gap-2 px-4">
              {busy ? <Spinner className="size-3.5" /> : null}
              {oauth ? `Continue to ${entry ? site : "sign in"}` : "Add server"}
              {oauth ? <ArrowUpRight className="size-3" /> : null}
            </Button>
          }
        />
      </form>
    </Panel>
  );
}

// ── A server's tools ──────────────────────────────────────────────────────

/** Tools shown before "Show N more" (Paper 43). */
const TOOLS_FIRST = 6;

const TAG: Record<Tone, string> = {
  moss: "text-moss shadow-[inset_0_0_0_1px_rgb(63_107_78/0.35)]",
  copper: "text-copper shadow-[inset_0_0_0_1px_rgb(154_91_52/0.35)]",
  red: "text-destructive shadow-[inset_0_0_0_1px_rgb(162_59_47/0.35)]",
  grey: "text-muted-foreground shadow-[inset_0_0_0_1px_rgb(31_42_68/0.14)]",
};

function ToolRow({
  tool,
  server,
  busy,
  onToggle,
}: {
  tool: McpTool;
  /** Its server, for the owner's Ask first under a tool that's on and changes things. */
  server?: McpServer;
  busy: boolean;
  onToggle: (on: boolean) => void;
}) {
  const tag = toolTag(tool);
  return (
    <div data-tool={tool.name} className="flex items-center gap-4 py-2.5">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[13px] font-medium break-all text-foreground">
            {tool.name}
          </span>
          <span className={cn("rounded-tag px-1.5 text-xs leading-[18px]", TAG[tag.tone])}>
            {tag.text}
          </span>
        </span>
        {tool.description ? (
          <span className="line-clamp-2 text-meta leading-[18px] text-muted-foreground">
            {tool.description}
          </span>
        ) : null}
        {server ? <ToolAskFirst server={server} tool={tool} /> : null}
      </div>
      <Switch
        aria-label={`${tool.name}: ${tool.enabled ? "on" : "off"}`}
        checked={tool.enabled}
        disabled={busy}
        onCheckedChange={(on) => onToggle(on)}
        className="h-6 w-10.5 [&>span]:size-5"
      />
    </div>
  );
}

/**
 * A server's guide (slice 6k §5; Paper 43e, 43f), above its tools and shaped
 * like them. A new version is read before it can be used, so what's accepted is
 * what was read.
 */
export function GuideRow({
  guide,
  server,
  several,
  owner,
  busy,
  onToggle,
  onRead,
}: {
  guide: McpGuide;
  server: string;
  /** The server has more than one: each says which it is. */
  several: boolean;
  owner: boolean;
  busy: boolean;
  onToggle: (on: boolean) => void;
  onRead: () => void;
}) {
  const title = several ? `Guide: ${guide.name}` : `Guide from ${server}`;
  return (
    <div
      data-guide={guide.name}
      className="flex items-start gap-4 border-t border-border px-5 py-3.5 md:items-center md:px-6"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-sm leading-5 font-semibold text-foreground">{title}</span>
        {guide.description ? (
          <span className="line-clamp-3 text-meta leading-[18px] text-muted-foreground">
            {guide.description}
          </span>
        ) : null}
        {guide.changed ? (
          <span className="pt-1.5 text-meta leading-[18px] text-copper">
            {server} changed it. Your crew keeps this version until you choose.
          </span>
        ) : null}
        <button
          type="button"
          onClick={onRead}
          className="flex min-h-11 w-fit items-center text-meta leading-[18px] font-medium text-foreground underline underline-offset-2 md:min-h-0 md:pt-1"
        >
          {guide.changed ? "Read the new version" : "Read the guide"}
        </button>
      </div>
      <Switch
        aria-label={`${title}: ${guide.enabled ? "on" : "off"}`}
        checked={guide.enabled}
        disabled={busy || !owner}
        onCheckedChange={(on) => onToggle(on)}
        className="h-6 w-10.5 [&>span]:size-5"
      />
    </div>
  );
}

/** The text in view: the new version when one is waiting. One tab a file. */
export function GuideText({
  guide,
  path,
  onPath,
}: {
  guide: McpGuideText;
  path: string;
  onPath: (path: string) => void;
}) {
  const files = guide.pending?.files ?? guide.files;
  const file = files.find((f) => f.path === path) ?? files[0];
  return (
    <>
      {files.length > 1 ? (
        <div className="flex flex-wrap gap-1.5 border-t border-border px-4 py-2 md:px-6">
          {files.map((f) => (
            <button
              key={f.path}
              type="button"
              aria-pressed={f.path === file?.path}
              onClick={() => onPath(f.path)}
              className={cn(
                "min-h-9 rounded-tag px-2.5 font-mono text-xs leading-[18px] md:min-h-0 md:py-1",
                f.path === file?.path
                  ? "bg-muted font-medium text-foreground"
                  : "text-muted-foreground",
              )}
            >
              {guideTab(f.path)}
            </button>
          ))}
        </div>
      ) : null}
      <div
        data-guide-text
        className="max-h-[46dvh] overflow-y-auto border-t border-border px-5 py-4 md:max-h-[420px] md:px-6"
      >
        <DocMarkdown className="text-sm leading-[22px] [&_h1]:text-base [&_h1]:font-semibold [&_h2]:text-sm [&_h2]:font-semibold [&_h3]:text-sm [&_h3]:font-semibold">
          {guideBody(file?.text ?? "")}
        </DocMarkdown>
      </div>
    </>
  );
}

/** A guide's text, or what stands for it until it's read. */
function GuideBody({
  read,
  path,
  onPath,
}: {
  read: ReturnType<typeof useGuideText>;
  path: string;
  onPath: (path: string) => void;
}) {
  if (read.data) return <GuideText guide={read.data.guide} path={path} onPath={onPath} />;
  return (
    <div className="flex justify-center py-10">
      {read.isError ? (
        <Button variant="outline" onClick={() => void read.refetch()}>
          Try again
        </Button>
      ) : (
        <Spinner />
      )}
    </div>
  );
}

/** A new version's choice, or Done when there's nothing to choose. */
function GuideFooter({
  waiting,
  busy,
  onAccept,
  onBack,
}: {
  waiting: boolean;
  busy: boolean;
  onAccept: () => void;
  onBack: () => void;
}) {
  if (!waiting) {
    return (
      <Footer
        note=""
        busy={false}
        onClose={onBack}
        secondary={null}
        primary={
          <Button size="lg" onClick={onBack}>
            Done
          </Button>
        }
      />
    );
  }
  return (
    <Footer
      note="Your crew keeps the old one for now."
      busy={busy}
      onClose={onBack}
      secondary={
        <Button variant="ghost" size="lg" className="h-11 md:h-9" disabled={busy} onClick={onBack}>
          Not now
        </Button>
      }
      primary={
        <Button size="lg" disabled={busy} onClick={onAccept}>
          {busy ? <Spinner className="size-3.5" /> : null}
          Use the new guide
        </Button>
      }
    />
  );
}

/** Reading a guide (Paper 43g, 43h): its words, and a new version's choice. */
function GuideReader({
  server,
  guideId,
  onBack,
}: {
  server: McpServer;
  guideId: string;
  onBack: () => void;
}) {
  const read = useGuideText(server.id, guideId);
  const change = useChangeGuide(server.id);
  const [path, setPath] = useState("SKILL.md");
  const pending = read.data?.guide.pending;
  const error = change.error ?? read.error;
  const accept = () =>
    pending && change.mutate({ id: guideId, accept_digest: pending.digest }, { onSuccess: onBack });
  return (
    <div data-guide-reader={guideId}>
      <button
        type="button"
        onClick={onBack}
        className="flex min-h-11 items-center gap-1 px-5 pt-3 text-meta font-medium text-foreground md:min-h-0 md:px-6 md:pt-6"
      >
        <ChevronLeft className="size-3.5" />
        {server.name}’s tools
      </button>
      <div className="-mt-3 md:-mt-4">
        <Head
          icon={false}
          title={`${pending ? "New guide" : "Guide"} from ${server.name}`}
          line={`These are ${server.name}’s own words. They never change what your crew is allowed to do.`}
        />
      </div>
      {error ? (
        <p role="alert" className="px-5 pb-3 text-sm text-destructive md:px-6">
          {errorHint(error)}
        </p>
      ) : null}
      <GuideBody read={read} path={path} onPath={setPath} />
      <GuideFooter
        waiting={Boolean(pending)}
        busy={change.isPending}
        onAccept={() => accept()}
        onBack={onBack}
      />
    </div>
  );
}

/** The list, the shortcuts and Show N more, apart from the frame so a test can render it. */
export function ToolList({
  tools,
  server,
  owner,
  busy,
  onToggle,
  onShortcut,
}: {
  tools: McpTool[];
  server?: McpServer;
  owner: boolean;
  busy: boolean;
  onToggle: (tool: McpTool, on: boolean) => void;
  onShortcut: (which: "all" | "read_only") => void;
}) {
  const [more, setMore] = useState(false);
  const shown = more ? tools : tools.slice(0, TOOLS_FIRST);
  const hidden = tools.slice(TOOLS_FIRST);
  const hiddenOn = hidden.filter((t) => t.enabled).length;
  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-2.5 border-y border-border px-5 py-3 md:flex-row md:items-center md:px-6">
        <span className="flex-1 text-sm font-semibold text-foreground" data-tool-counts>
          {toolCounts(tools)}
        </span>
        {owner ? (
          <div className="grid grid-cols-2 gap-2 md:flex">
            <Button
              variant="outline"
              className="bg-card"
              disabled={busy}
              onClick={() => onShortcut("read_only")}
            >
              Read-only tools only
            </Button>
            <Button
              variant="outline"
              className="bg-card"
              disabled={busy}
              onClick={() => onShortcut("all")}
            >
              Turn all on
            </Button>
          </div>
        ) : null}
      </div>
      <div className="flex flex-col px-5 py-2 md:px-6">
        {tools.length === 0 ? (
          <p className="py-3 text-sm text-muted-foreground">This server lists no tools.</p>
        ) : null}
        {shown.map((t) => (
          <ToolRow
            key={t.id}
            tool={t}
            server={server}
            busy={busy || !owner}
            onToggle={(on) => onToggle(t, on)}
          />
        ))}
        {!more && hidden.length > 0 ? (
          <button
            type="button"
            onClick={() => setMore(true)}
            className="flex items-center gap-2 py-3 text-left text-meta"
          >
            <span className="font-medium text-foreground underline underline-offset-2">
              Show {hidden.length} more
            </span>
            <span className="text-muted-foreground">
              {hiddenOn} on, {hidden.length - hiddenOn} off
            </span>
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** A server's tools, and what changes them: one switch, a shortcut, Refresh tools. */
function useToolsWindow(server: McpServer, timeZone: string) {
  const list = useServerTools(server.id);
  const setTool = useSetTool(server.id);
  const changeGuide = useChangeGuide(server.id);
  const shortcut = useSetTools(server.id);
  const refresh = useRefreshServer();
  const data = list.data;
  return {
    tools: data ? data.tools : null,
    guides: data?.guides ?? [],
    setGuide: (id: string, enabled: boolean) => changeGuide.mutate({ id, enabled }),
    guideBusy: changeGuide.isPending,
    /** The list never loaded: its error shows, with Try again, not a spinner (Devin on #249). */
    failed: list.isError && !data,
    retry: () => void list.refetch(),
    busy: shortcut.isPending || refresh.isPending,
    refreshing: refresh.isPending,
    error: [setTool, shortcut, refresh, changeGuide, list].map((q) => q.error).find(Boolean),
    note: toolsNote((data ? data.server : server).synced_at, timeZone),
    toggle: (t: McpTool, enabled: boolean) => setTool.mutate({ id: t.id, enabled }),
    shortcut: (which: "all" | "read_only") => shortcut.mutate(which),
    refresh: () => refresh.mutate(server.id),
  };
}

/** A server's tools (Paper 43): each On or Off, the shortcuts, and Refresh tools. */
export function ServerToolsWindow({
  server,
  owner,
  timeZone,
  onClose,
}: {
  server: McpServer | null;
  owner: boolean;
  timeZone: string;
  onClose: () => void;
}) {
  return (
    <Panel open={server !== null} onClose={onClose} width={640}>
      {server ? (
        <ToolsBody server={server} owner={owner} timeZone={timeZone} onClose={onClose} />
      ) : null}
    </Panel>
  );
}

function ToolsBody({
  server,
  owner,
  timeZone,
  onClose,
}: {
  server: McpServer;
  owner: boolean;
  timeZone: string;
  onClose: () => void;
}) {
  const w = useToolsWindow(server, timeZone);
  const [reading, setReading] = useState<string | null>(null);
  if (reading) {
    return <GuideReader server={server} guideId={reading} onBack={() => setReading(null)} />;
  }
  return (
    <div data-server-tools={server.id}>
      <Head
        title={`${server.name}'s tools`}
        line="Your crew can find and call the tools that are on. Tools that change things start off, and ask first when you turn one on: untick Ask first once you trust it."
      />
      {w.error ? (
        <p role="alert" className="px-5 pb-3 text-sm text-destructive md:px-6">
          {errorHint(w.error)}
        </p>
      ) : null}
      {w.guides.map((g) => (
        <GuideRow
          key={g.id}
          guide={g}
          server={server.name}
          several={w.guides.length > 1}
          owner={owner}
          busy={w.guideBusy}
          onToggle={(on) => w.setGuide(g.id, on)}
          onRead={() => setReading(g.id)}
        />
      ))}
      {w.tools ? (
        <ToolList
          tools={w.tools}
          server={server}
          owner={owner}
          busy={w.busy}
          onToggle={w.toggle}
          onShortcut={w.shortcut}
        />
      ) : (
        <div className="flex justify-center py-10">
          {w.failed ? (
            <Button variant="outline" onClick={w.retry}>
              Try again
            </Button>
          ) : (
            <Spinner />
          )}
        </div>
      )}
      <Footer
        note={w.note}
        busy={w.busy}
        onClose={onClose}
        secondary={
          owner ? <RefreshButton busy={w.busy} spinning={w.refreshing} onClick={w.refresh} /> : null
        }
        primary={
          <Button size="lg" onClick={onClose}>
            Done
          </Button>
        }
      />
    </div>
  );
}

function RefreshButton({
  busy,
  spinning,
  onClick,
}: {
  busy: boolean;
  spinning: boolean;
  onClick: () => void;
}) {
  return (
    <Button variant="ghost" size="lg" disabled={busy} className="h-11 md:h-9" onClick={onClick}>
      {spinning ? <Spinner className="size-3.5" /> : null}
      Refresh tools
    </Button>
  );
}

// ── Reconnect with a new key ──────────────────────────────────────────────

/** Reconnect for an API-key server: a new key replaces the old (spec §6.2). */
export function NewKeyWindow({
  server,
  onClose,
}: {
  server: McpServer | null;
  onClose: () => void;
}) {
  const reconnect = useReconnectServer();
  const [key, setKey] = useState("");
  useEffect(() => {
    setKey("");
    reconnect.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [server?.id]);
  const busy = reconnect.isPending;
  return (
    <Panel open={server !== null} locked={busy} onClose={onClose}>
      {server ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!key.trim()) return;
            reconnect.mutate({ id: server.id, api_key: key.trim() }, { onSuccess: onClose });
          }}
        >
          <Head title={`Reconnect ${server.name}`} line="A new key replaces the old one." />
          <div className="flex flex-col gap-3 px-5 pb-5 md:px-6">
            {reconnect.error ? (
              <p role="alert" className="text-sm text-destructive">
                {errorHint(reconnect.error)}
              </p>
            ) : null}
            <Field label="API key">
              <Input
                aria-label="API key"
                type="password"
                autoComplete="off"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                className="h-10"
              />
            </Field>
          </div>
          <Footer
            note="Its tools are read again with the new key."
            busy={busy}
            onClose={onClose}
            primary={
              <Button type="submit" size="lg" disabled={busy || !key.trim()}>
                Save key
              </Button>
            }
          />
        </form>
      ) : null}
    </Panel>
  );
}
